import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraIconButton } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQueryClient } from '@tanstack/react-query';
import { AlertTriangle, ArrowDown, Brain, PanelLeftClose, PanelLeftOpen, PanelRight, RotateCcw, Settings, X } from 'lucide-react';
import { memo, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { ArtifactDrawerProvider, useArtifactDrawer } from '../../contexts/ArtifactDrawerContext';
import { asOfficeDocType, OfficeDocumentDrawerProvider, useOfficeDocumentDrawer } from '../../contexts/OfficeDocumentDrawerContext';
import { OnlyOfficeConnectorProvider } from '../../contexts/OnlyOfficeConnectorContext';
import { useArtifactSync } from '../../hooks/useArtifactSync';
import { useChatAccess } from '../../hooks/useChatAccess';
import { conversationQueryKey, useConversation } from '../../hooks/useConversation';
import { memoryChipLabel, useMemories } from '../../hooks/useMemories';
import { useShanksChatConnect } from '../../hooks/useShanksChatConnect';
import { useStreamCompleteSound } from '../../hooks/useStreamCompleteSound';
import { artifactDataSchema } from '../../lib/artifacts/types';
import { applyConversationTitle } from '../../lib/chat/applyConversationTitle';
import { canShowPinPicker, MAX_CHAT_PINS, parseChatOrigin, pinKey } from '../../lib/chat/envelope';
import { memoryScopesFromEnvelope } from '../../lib/chat/memoryScope';
import { shanksConversationId as toShanksConversationId } from '../../lib/chat/shanks';
import { projectDataClient } from '../../lib/connect/clients';
import { convertProtoConversation } from '../../lib/connect/protoToChatHistory';
import { useChatArtifactStore } from '../../lib/stores/ChatArtifactStore';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
import { emitToolEvent, useToolEvents } from '../../lib/tool-stream';
import { cn } from '../../lib/utils';
import { ArtifactEditorDrawer } from '../artifacts/ArtifactEditorDrawer';
import { ChatSettings } from '../ChatSettings';
import { ComposerInput } from '../ComposerInput';
import { MemoryPanel } from '../MemoryPanel';
import { ChatPins, DEFAULT_PIN_OPTIONS } from './ChatPins';
import { ChatProjectBadge } from './ChatProjectBadge';
import { ChatThread } from './ChatThread';
import { ChatTitle } from './ChatTitle';
import { OfficeDocumentDrawer } from './OfficeDocumentDrawer';
import { ShareButton } from './ShareButton';
import { isOfficeMintTool } from './shanks-tools/CreateOfficeDocumentCard';
import { mergeMessagesById } from './shanksMessageAdapter';
import { ViewOnlyBanner } from './ViewOnlyBanner';
/**
 * Convert Shanks messages to ChatMessage format for ChatThread compatibility
 * Tool calls are converted to proper MessagePartTool parts for rendering
 */ // Build a renderable tool-call part from a ShanksToolCall. Same shape as the
// inline conversion below; extracted so the ordered-parts path and the
// fallback path don't drift.
const buildToolCallPart = (tc)=>{
    let state;
    if (tc.result || tc.error) {
        state = 'output-available';
    } else if (tc.arguments) {
        state = 'input-available';
    } else {
        state = 'input-streaming';
    }
    let output;
    if (tc.result) {
        try {
            output = JSON.parse(tc.result);
        } catch  {
            output = tc.result;
        }
    }
    if (tc.error) {
        output = {
            error: tc.error
        };
    }
    // Cast through unknown to bypass strict ai SDK type requirements;
    // MessagePartRenderer handles this custom tool part format.
    return {
        type: `tool-${tc.name}`,
        toolName: tc.name,
        toolCallId: tc.id,
        state,
        output,
        input: parseToolArguments(tc.arguments),
        purpose: tc.purpose,
        executionState: tc.executionState,
        duration: tc.duration
    };
};
function parseToolArguments(argumentsJson) {
    if (!argumentsJson) return undefined;
    try {
        return JSON.parse(argumentsJson);
    } catch  {
        return undefined;
    }
}
const convertShanksToChatMessages = (shanksMessages)=>{
    return shanksMessages.map((msg)=>{
        const parts = [];
        const toolCallLookup = new Map((msg.toolCalls ?? []).map((tc)=>[
                tc.id,
                tc
            ]));
        const renderedToolIds = new Set();
        // Ordered-parts path: preserves interleaving of reasoning / text / tool
        // calls in the exact order the provider streamed them. Mirrors
        // AISidebarShell's conversion at uis/thousand-sunny/src/components/
        // ProposalDashboard/v2/AISidebarShell.tsx so reasoning models render
        // their chain-of-thought in narrative order.
        if (msg.parts && msg.parts.length > 0) {
            for (const p of msg.parts){
                if (p.type === 'reasoning') {
                    parts.push({
                        type: 'reasoning',
                        text: p.content,
                        state: 'done'
                    });
                } else if (p.type === 'text') {
                    parts.push({
                        type: 'text',
                        text: p.content
                    });
                } else if (p.type === 'tool_call' && p.toolCallId) {
                    const tc = toolCallLookup.get(p.toolCallId);
                    if (tc) {
                        renderedToolIds.add(tc.id);
                        parts.push(buildToolCallPart(tc));
                    }
                }
            }
        }
        // Fallback for messages without an ordered parts array (e.g. loaded from
        // history, or produced by older code paths). Keep the prior fixed order:
        // tool calls → reasoning → text. Skips any tool calls already rendered
        // above so messages with partial parts don't double up.
        if (!msg.parts || msg.parts.length === 0) {
            for (const tc of msg.toolCalls ?? []){
                parts.push(buildToolCallPart(tc));
            }
            if (msg.reasoning) {
                parts.push({
                    type: 'reasoning',
                    text: msg.reasoning,
                    state: 'done'
                });
            }
            if (msg.content) {
                parts.push({
                    type: 'text',
                    text: msg.content
                });
            }
        } else {
            // Append any tool calls that weren't represented in the ordered parts
            // array (defensive: streams that complete tools after the part was
            // pushed still need their final state surfaced).
            for (const tc of msg.toolCalls ?? []){
                if (renderedToolIds.has(tc.id)) continue;
                parts.push(buildToolCallPart(tc));
            }
        }
        return {
            id: msg.id,
            role: msg.role,
            parts,
            metadata: {
                createdAt: new Date().toISOString()
            }
        };
    });
};
// ============================================================================
// Component
// ============================================================================
// Constants for scroll behavior and timing
// Pixel distance from bottom before considering user "at bottom" for auto-scroll
const SCROLL_BOTTOM_THRESHOLD = 80;
// Maximum allowed prompt length from sessionStorage for security
// Prevents DoS attacks and excessive memory usage from malicious/corrupted sessionStorage data
// 10k characters is sufficient for most user prompts while being conservative on memory
const MAX_PROMPT_LENGTH = 10000;
/**
 * Main chat view component that handles message streaming, scrolling, and artifacts
 * @param props Component props
 * @returns Chat view component
 */ /**
 * Validates if a chatId is safe and follows expected format
 * @param id - The chat ID to validate
 * @returns True if the chatId is valid
 */ const isValidChatId = (id)=>{
    // Chat IDs should be alphanumeric with hyphens/underscores only
    // Prevents path traversal and injection attacks
    return /^[a-zA-Z0-9_-]+$/.test(id);
};
const ChatViewContent = /*#__PURE__*/ memo(function ChatViewContent({ chatId, projectId, initialMessages, onConversationCreated, readOnly, onClose, variant = 'default' }) {
    const queryClient = useQueryClient();
    // Validate chatId for security
    if (!chatId || !isValidChatId(chatId)) {
        throw new Error('Invalid chat ID format');
    }
    const { isOpen, closeDrawer, openDrawer } = useArtifactDrawer();
    // Get chat access context (shared vs owned) - skip when readOnly is explicitly set (e.g., project chats)
    const skipAccessCheck = readOnly !== undefined;
    const { accessContext, loading: accessLoading, error: accessError } = useChatAccess({
        chatId,
        skip: skipAccessCheck
    });
    // When readOnly is explicitly set, use it directly. Otherwise check shared chat access.
    const isViewOnly = skipAccessCheck ? readOnly === true : accessLoading || !!accessError || !accessContext?.isOwner;
    const isOwner = !isViewOnly && (accessContext?.isOwner ?? true);
    // Header chrome. The chat-list rail only exists on the /assistant shell, so
    // its toggle is hidden everywhere else this view is embedded.
    const location = useLocation();
    const showRailToggle = location.pathname.startsWith('/assistant');
    const isSharedChat = Boolean(accessContext?.isShared);
    const showPins = canShowPinPicker({
        pathname: location.pathname,
        projectId,
        isOwner: isOwner && !isViewOnly,
        isShared: isSharedChat
    });
    const [pins, setPins] = useState(()=>{
        try {
            const raw = window.localStorage.getItem(`specra.chat.pins:${chatId}`);
            return raw ? JSON.parse(raw) : [];
        } catch  {
            return [];
        }
    });
    const [captureList, setCaptureList] = useState([]);
    // Pursuits load when the picker first opens, not on every chat mount: most
    // chats never open it, and a caller without a tenant-root capture grant
    // gets a guaranteed PermissionDenied (SAD-426) — which the picker then
    // states instead of silently offering fewer options.
    const [pinPickerOpened, setPinPickerOpened] = useState(false);
    const [captureNotice, setCaptureNotice] = useState();
    const [captureLoading, setCaptureLoading] = useState(false);
    useEffect(()=>{
        if (!showPins || !pinPickerOpened) return;
        let cancelled = false;
        setCaptureLoading(true);
        projectDataClient.listCaptureProjects({}).then((resp)=>{
            if (!cancelled) setCaptureList(resp.captures ?? []);
        }).catch((err)=>{
            if (cancelled) return;
            setCaptureList([]);
            setCaptureNotice(ConnectError.from(err).code === Code.PermissionDenied ? "Pursuits aren't available — you don't have capture access." : "Pursuits couldn't be loaded.");
        }).finally(()=>{
            if (!cancelled) setCaptureLoading(false);
        });
        return ()=>{
            cancelled = true;
        };
    }, [
        showPins,
        pinPickerOpened
    ]);
    const pinOptions = useMemo(()=>{
        const fromCaptures = captureList.map((c)=>({
                label: c.name || 'Pursuit',
                pin: {
                    surface: 'capture_pursuit',
                    pursuitId: c.captureProjectId
                }
            }));
        return [
            ...fromCaptures,
            ...DEFAULT_PIN_OPTIONS
        ];
    }, [
        captureList
    ]);
    useEffect(()=>{
        const seed = location.state?.seedPins;
        if (!seed?.length) return;
        setPins((prev)=>{
            const next = [
                ...prev
            ];
            for (const p of seed){
                if (!next.some((x)=>pinKey(x) === pinKey(p))) next.push(p);
            }
            return next.slice(0, MAX_CHAT_PINS);
        });
    }, [
        location.state
    ]);
    useEffect(()=>{
        if (!showPins) return;
        try {
            window.localStorage.setItem(`specra.chat.pins:${chatId}`, JSON.stringify(pins));
        } catch  {
        // ignore quota / private mode
        }
    }, [
        chatId,
        pins,
        showPins
    ]);
    const chatRailCollapsed = useLayoutStore((s)=>s.chatRailCollapsed);
    const toggleChatRail = useLayoutStore((s)=>s.toggleChatRailCollapsed);
    const setAssistOpen = useLayoutStore((s)=>s.setAssistOpen);
    const setAssistOrigin = useLayoutStore((s)=>s.setAssistOrigin);
    // "To sidebar" opens the app-wide Assist rail (⌘J) anchored to this route,
    // leaving the full-page thread open behind it.
    const handleToSidebar = useCallback(()=>{
        setAssistOrigin(location.pathname);
        setAssistOpen(true);
    }, [
        location.pathname,
        setAssistOpen,
        setAssistOrigin
    ]);
    // Enable cross-tab artifact sync
    useArtifactSync();
    const playCompletionSound = useStreamCompleteSound();
    const [isSending, setIsSending] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [showMemoryPanel, setShowMemoryPanel] = useState(false);
    const [memoryDraft, setMemoryDraft] = useState();
    // Drives the jump-to-latest button: shown only when the thread actually
    // overflows AND the user has scrolled up. isAtBottomRef (below) stays for the
    // imperative auto-scroll paths.
    const [showScrollButton, setShowScrollButton] = useState(false);
    // sr-only streaming status for screen readers (see the isSending effect).
    const [liveStatus, setLiveStatus] = useState('');
    // Surface the live title in the header. Shares the cache key used by
    // ChatSettings (via the same useConversation hook) so a Save in the
    // modal's optimistic write lights up the header heading without a
    // refetch round-trip.
    const [conversationPersisted, setConversationPersisted] = useState(()=>{
        try {
            return !sessionStorage.getItem(`init::${chatId}`);
        } catch  {
            return true;
        }
    });
    const conversationQuery = useConversation(conversationPersisted ? chatId : '');
    const savedSubmission = conversationQuery.data?.submissions?.find((submission)=>submission.state === 'running') ?? conversationQuery.data?.submissions?.[0];
    const restoredSubmission = useMemo(()=>savedSubmission?.message ? {
            id: savedSubmission.submissionId,
            content: savedSubmission.message,
            fileIds: savedSubmission.fileIds,
            state: savedSubmission.state
        } : undefined, [
        savedSubmission
    ]);
    const remoteRunning = !isViewOnly && savedSubmission?.state === 'running';
    const restoredFailure = !isViewOnly && savedSubmission && [
        'interrupted',
        'failed',
        'incomplete'
    ].includes(savedSubmission.state);
    const responseBusy = isSending || remoteRunning;
    // displayTitle is the single source of truth for both the <h1> in the
    // header and the chatTitle prop on ShareButton. Folding the fallback
    // here (instead of doing `title || 'Chat'` at each call site) keeps
    // the two in sync — a different fallback on either side would cause
    // a brief flash where the header is empty but the share dialog says
    // "Chat" (or vice versa) during the load window.
    const conversationTitle = conversationQuery.data?.conversation?.title ?? '';
    const displayTitle = conversationTitle || 'Chat';
    const scrollContainerRef = useRef(null);
    const isAtBottomRef = useRef(true);
    const hasSubmittedRef = useRef(false);
    const isMountedRef = useRef(true);
    // Tracks whether a stream was in flight, so the status announcer only says
    // "Response complete" after an actual response — not on initial mount.
    const wasSendingRef = useRef(false);
    const pausedForApprovalRef = useRef(false);
    const { events: saveEvents, clear: clearSaveEvents } = useToolEvents((ev)=>ev.type === 'data-chat-log-save');
    // Handle artifact created by Shanks - add to store and open drawer
    const handleShanksArtifact = useCallback((artifact)=>{
        const store = useChatArtifactStore.getState();
        store.addArtifact({
            id: artifact.id,
            title: artifact.title,
            kind: artifact.kind,
            content: artifact.content || '',
            sourceAttachmentId: artifact.sourceAttachmentId,
            sourceUrl: artifact.sourceUrl,
            contentType: artifact.contentType,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
        });
        // Select the artifact in the store so the drawer can display it
        store.selectArtifact(artifact.id);
        // Open the drawer to show the new artifact
        openDrawer(artifact.id, {
            sourceContext: 'chat',
            chatId
        });
    }, [
        chatId,
        openDrawer
    ]);
    // Apply a server-generated conversation title live. Mirrors the ChatSettings
    // optimistic dual-write: the conversation cache drives the header <h1> +
    // ShareButton (via useConversation), and the ['chats'] list backs the
    // switcher / history. No refetch — shanks already persisted the title.
    const handleShanksTitle = useCallback((title)=>{
        applyConversationTitle(queryClient, chatId, title);
    }, [
        queryClient,
        chatId
    ]);
    // Extract conversation ID from chatId (remove "shanks-" prefix if present).
    // Non-prefixed ids (zou project chats' chat_…, bare /assistant uuids) fall
    // back to the chatId itself: shanks honors a caller-supplied conversation id
    // (agent.go Chat only mints a uuid when the field is empty), and the read
    // path (GetConversation via bareChatId) already keys history by this same
    // id. Passing '' here instead orphaned every non-shanks-prefixed thread —
    // the first message minted a fresh server-side id nothing ever linked back.
    const shanksConversationId = useMemo(()=>toShanksConversationId(chatId) ?? chatId, [
        chatId
    ]);
    // Desk-bound / project chats stay single-bind. Pins only on personal
    // unshared /assistant (W0-E).
    const chatEnvelope = useMemo(()=>{
        const parsed = parseChatOrigin(location.pathname);
        const surface = parsed.surface === 'root' ? 'assistant' : parsed.surface;
        return {
            ...parsed,
            surface,
            projectId: projectId || parsed.projectId,
            pins: showPins ? pins : undefined
        };
    }, [
        location.pathname,
        projectId,
        showPins,
        pins
    ]);
    const memoryScopes = useMemo(()=>memoryScopesFromEnvelope(chatEnvelope), [
        chatEnvelope
    ]);
    const memoriesQuery = useMemories(memoryScopes);
    const memoryChip = useMemo(()=>memoryChipLabel(memoriesQuery.data ?? []), [
        memoriesQuery.data
    ]);
    const handleRememberDraft = useCallback((text)=>{
        const trimmed = text.trim();
        if (!trimmed) return;
        const firstLine = trimmed.split('\n')[0]?.trim() ?? 'Note';
        const title = firstLine.length > 80 ? `${firstLine.slice(0, 77)}...` : firstLine;
        setMemoryDraft({
            title,
            body: trimmed
        });
        setShowMemoryPanel(true);
    }, []);
    // Chat is driven by the Connect-Web hook end-to-end:
    // browser → sabaody → shanks → pangaea.
    const shanksChat = useShanksChatConnect({
        chatId,
        restoredSubmission,
        initialConversationId: shanksConversationId,
        projectId,
        context: chatEnvelope,
        onFinish: ()=>{
            setIsSending(false);
            queryClient.invalidateQueries({
                queryKey: conversationQueryKey(chatId)
            });
            playCompletionSound();
            // A brand-new chat is only persisted server-side once its first turn
            // completes; applyConversationTitle can't surface it (it only patches
            // existing ['chats'] rows), so refetch the list here to pull the new
            // conversation into the explore rail without a manual reload.
            queryClient.invalidateQueries({
                queryKey: [
                    'chats'
                ]
            });
            // A new chat 404s on mount, so its conversation cache is empty and the
            // streamed title event had nothing to patch — the header stays "Chat".
            // The row is persisted by now, so refetch it to light up the <h1>. Gated
            // on an empty cache: existing chats already patched their title
            // optimistically and shouldn't pay a GetConversation on every turn.
            if (!queryClient.getQueryData(conversationQueryKey(chatId))) {
                queryClient.invalidateQueries({
                    queryKey: conversationQueryKey(chatId)
                });
            }
        },
        onAwaitingApproval: ()=>{
            // Write-approval pause: release the composer without the end-of-job
            // completion sound or chat-list refetch — the user still owes an Accept.
            pausedForApprovalRef.current = true;
            setIsSending(false);
            setLiveStatus('Awaiting approval');
            queryClient.invalidateQueries({
                queryKey: conversationQueryKey(chatId)
            });
        },
        onError: ()=>{
            setIsSending(false);
            queryClient.invalidateQueries({
                queryKey: conversationQueryKey(chatId)
            });
        },
        onConversationId: (id)=>{
            setConversationPersisted(true);
            onConversationCreated?.();
            queryClient.invalidateQueries({
                queryKey: conversationQueryKey(id)
            });
        },
        onArtifact: handleShanksArtifact,
        onTitle: handleShanksTitle
    });
    // Messages - convert Shanks messages to ChatMessage format
    const messages = useMemo(()=>{
        return convertShanksToChatMessages(shanksChat.messages);
    }, [
        shanksChat.messages
    ]);
    // A deliberate Stop is recoverable status, not a failure notice.
    const stoppedSubmission = !shanksChat.error && restoredFailure && savedSubmission.stopRequested;
    const incompleteSubmission = shanksChat.incomplete || !shanksChat.error && restoredFailure && savedSubmission.state === 'incomplete';
    // Error state
    const error = responseBusy ? null : shanksChat.error ? new Error(shanksChat.error) : restoredFailure ? new Error(stoppedSubmission ? 'You stopped this response. Retry when you are ready to continue.' : incompleteSubmission ? 'This request needs more work. Your progress is saved. Continue to finish it.' : savedSubmission.state === 'interrupted' ? 'This response was interrupted. Retry to continue your saved request.' : 'This response could not be completed. Retry your saved request.') : null;
    // Merged thread — loaded history + the live streaming buffer, deduped by
    // message id (see mergeMessagesById). Shared by ChatThread and the
    // fullscreen artifact drawer's compact chat so both render one thread.
    const thread = useMemo(()=>{
        const persisted = conversationQuery.data?.messages?.length ? convertProtoConversation(conversationQuery.data.conversation, conversationQuery.data.messages).messages : initialMessages || [];
        return mergeMessagesById(persisted, messages, isSending);
    }, [
        conversationQuery.data,
        initialMessages,
        messages,
        isSending
    ]);
    // Send message function
    const sendMessage = useCallback(async (messageData)=>{
        // Extract text content from parts
        const textPart = messageData.parts.find((p)=>p.type === 'text');
        const content = textPart?.text || '';
        // Extract file IDs from attachments
        const attachmentsPart = messageData.parts.find((p)=>p.type === 'data-attachments');
        const fileIds = attachmentsPart?.data?.map((att)=>att.id).filter((id)=>!!id);
        await shanksChat.sendMessage(content, {
            fileIds
        });
    }, [
        shanksChat
    ]);
    // When the user accepts/rejects/comments on a proposed document/record write,
    // continue THIS conversation with that outcome so the agent hears the result
    // and can proceed. Passed down through ChatThread (and the fullscreen artifact
    // drawer's compact chat) so the follow-up is scoped to this chat instead of
    // broadcast to every chat surface that happens to be mounted.
    const sendCardFeedback = useCallback((message)=>{
        if (isViewOnly) return;
        setIsSending(true);
        // Reset the sending flag if the follow-up never starts streaming; the
        // stream's own onError also resets it, this just covers a rejected send.
        shanksChat.sendMessage(message).catch(()=>setIsSending(false));
    }, [
        shanksChat,
        isViewOnly
    ]);
    // Auto-open the editor drawer when a document is minted in the LIVE stream
    // (create-office-document or a SAM awards Excel tool that returns documentId).
    // shanksChat.messages holds only this session's turns (loaded history arrives
    // via initialMessages), so this never fires for old mint results when
    // reopening a past conversation. Each doc opens at most once.
    const { openOffice: openOfficeDrawer } = useOfficeDocumentDrawer();
    // Track mint tool calls already handled (by tool-call id) so the effect
    // skips the JSON.parse for completed calls on every stream tick.
    const handledCreateCallsRef = useRef(new Set());
    useEffect(()=>{
        for (const msg of shanksChat.messages){
            for (const tc of msg.toolCalls ?? []){
                if (!isOfficeMintTool(tc.name) || !tc.result) continue;
                if (handledCreateCallsRef.current.has(tc.id)) continue;
                handledCreateCallsRef.current.add(tc.id);
                try {
                    const out = JSON.parse(tc.result);
                    if (out.success && out.documentId) {
                        openOfficeDrawer(out.documentId, {
                            title: out.name,
                            docType: asOfficeDocType(out.documentType)
                        });
                    }
                } catch  {
                // ignore unparseable tool results
                }
            }
        }
    }, [
        shanksChat.messages,
        openOfficeDrawer
    ]);
    const scrollToBottom = useCallback((behavior = 'auto')=>{
        const el = scrollContainerRef.current;
        if (!el) return;
        el.scrollTo({
            top: el.scrollHeight,
            behavior
        });
    }, []);
    // Recompute at-bottom + jump-button visibility from live metrics. The button
    // only shows when the thread genuinely overflows (short threads never scroll,
    // so no button) and the user is away from the bottom.
    const recomputeScrollState = useCallback(()=>{
        const el = scrollContainerRef.current;
        if (!el) return;
        const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
        const atBottom = distanceFromBottom < SCROLL_BOTTOM_THRESHOLD;
        const overflowing = el.scrollHeight - el.clientHeight > SCROLL_BOTTOM_THRESHOLD;
        setShowScrollButton(overflowing && !atBottom);
    }, []);
    // Track component mount status to prevent race conditions
    useEffect(()=>{
        isMountedRef.current = true;
        return ()=>{
            isMountedRef.current = false;
        };
    }, []);
    // Announce streaming lifecycle to screen readers via the sr-only status
    // region. Lifecycle only (not per-token) — polite re-announcements of a
    // growing node are noisy and inconsistent across ATs; the transcript stays
    // the navigable source of the actual answer. Gated on wasSendingRef so we
    // don't announce "complete" on mount.
    useEffect(()=>{
        if (responseBusy) {
            pausedForApprovalRef.current = false;
            setLiveStatus('Assistant is responding…');
            wasSendingRef.current = true;
        } else if (wasSendingRef.current) {
            if (!pausedForApprovalRef.current) {
                setLiveStatus('Response complete');
            }
            wasSendingRef.current = false;
            pausedForApprovalRef.current = false;
        }
    }, [
        responseBusy
    ]);
    // Layout/scroll anchoring also emits scroll events. Only an input gesture
    // may turn following off; reaching the bottom turns it back on.
    useEffect(()=>{
        const el = scrollContainerRef.current;
        if (!el) return;
        let touchY = 0;
        const onScroll = ()=>{
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 2) {
                isAtBottomRef.current = true;
            }
            if (isAtBottomRef.current) scrollToBottom();
            recomputeScrollState();
        };
        const onWheel = (event)=>{
            if (event.deltaY < 0) isAtBottomRef.current = false;
        };
        const onTouchStart = (event)=>{
            touchY = event.touches[0]?.clientY ?? 0;
        };
        const onTouchMove = (event)=>{
            const nextY = event.touches[0]?.clientY ?? touchY;
            if (nextY > touchY) isAtBottomRef.current = false;
            touchY = nextY;
        };
        const onKeyDown = (event)=>{
            const target = event.target;
            if (target.closest('input, textarea, button, [role="button"], [contenteditable="true"]')) return;
            if ([
                'ArrowUp',
                'PageUp',
                'Home'
            ].includes(event.key) || event.key === ' ' && event.shiftKey) {
                isAtBottomRef.current = false;
            }
        };
        const onPointerDown = (event)=>{
            // A scrollbar drag targets the scroll container, not an inline card.
            if (event.target === el && event.clientX >= el.getBoundingClientRect().right - 14) {
                isAtBottomRef.current = false;
            }
        };
        el.addEventListener('scroll', onScroll, {
            passive: true
        });
        el.addEventListener('wheel', onWheel, {
            passive: true
        });
        el.addEventListener('touchstart', onTouchStart, {
            passive: true
        });
        el.addEventListener('touchmove', onTouchMove, {
            passive: true
        });
        el.addEventListener('keydown', onKeyDown);
        el.addEventListener('pointerdown', onPointerDown);
        const raf = requestAnimationFrame(recomputeScrollState);
        return ()=>{
            cancelAnimationFrame(raf);
            el.removeEventListener('scroll', onScroll);
            el.removeEventListener('wheel', onWheel);
            el.removeEventListener('touchstart', onTouchStart);
            el.removeEventListener('touchmove', onTouchMove);
            el.removeEventListener('keydown', onKeyDown);
            el.removeEventListener('pointerdown', onPointerDown);
        };
    }, [
        recomputeScrollState,
        scrollToBottom
    ]);
    // New messages can commit after sendMessage returns control. Anchor to the
    // committed user turn, then follow its subsequent layout changes.
    const latestUserMessageId = [
        ...thread
    ].reverse().find((message)=>message.role === 'user')?.id;
    const previousUserMessageId = useRef(latestUserMessageId);
    const initiallyScrolledChat = useRef(null);
    useLayoutEffect(()=>{
        const firstHistoryRender = thread.length > 0 && initiallyScrolledChat.current !== chatId;
        if (!firstHistoryRender && previousUserMessageId.current === latestUserMessageId) return;
        if (thread.length > 0) initiallyScrolledChat.current = chatId;
        previousUserMessageId.current = latestUserMessageId;
        if (firstHistoryRender) isAtBottomRef.current = true;
        if (isAtBottomRef.current) scrollToBottom('auto');
    }, [
        chatId,
        thread.length,
        latestUserMessageId,
        scrollToBottom
    ]);
    useEffect(()=>{
        const el = scrollContainerRef.current;
        if (!el) return;
        const observer = new ResizeObserver(()=>{
            // Measure after scrolling: growth itself must not turn off following.
            if (isAtBottomRef.current) {
                scrollToBottom('auto');
            }
            recomputeScrollState();
        });
        observer.observe(el);
        if (el.firstElementChild) observer.observe(el.firstElementChild);
        return ()=>observer.disconnect();
    }, [
        scrollToBottom,
        recomputeScrollState
    ]);
    // Auto-send initial message from sessionStorage (when navigating from assistant page)
    useEffect(()=>{
        if (!isViewOnly && !hasSubmittedRef.current && messages.length === 0) {
            try {
                const key = `init::${chatId}`;
                const raw = typeof window !== 'undefined' ? sessionStorage.getItem(key) : null;
                if (raw) {
                    const parsed = JSON.parse(raw);
                    // Validate parsed data structure
                    if (!parsed || typeof parsed !== 'object') {
                        throw new Error('Invalid sessionStorage data: not an object');
                    }
                    const rawPrompt = typeof parsed.prompt === 'string' ? parsed.prompt.trim() : '';
                    // Sanitize and validate prompt content
                    // Silently truncate if exceeds max length (no need to log in production)
                    const prompt = rawPrompt.slice(0, MAX_PROMPT_LENGTH);
                    // Validate attachments array
                    const attachments = Array.isArray(parsed.attachments) ? parsed.attachments.filter((att)=>{
                        return att !== null && typeof att === 'object' && 'id' in att && 'name' in att && 'contentType' in att && typeof att.id === 'string' && typeof att.name === 'string' && typeof att.contentType === 'string';
                    }) : [];
                    if (prompt || attachments.length > 0) {
                        setIsSending(true);
                        // Build message parts
                        const parts = [];
                        if (prompt) {
                            parts.push({
                                type: 'text',
                                text: prompt
                            });
                        }
                        if (attachments.length > 0) {
                            parts.push({
                                type: 'data-attachments',
                                data: attachments
                            });
                        }
                        sendMessage({
                            parts
                        });
                        requestAnimationFrame(()=>scrollToBottom('auto'));
                    }
                    // Clean up sessionStorage
                    sessionStorage.removeItem(key);
                }
            } catch (error) {
                // Silently handle sessionStorage errors - data is optional
                if (process.env.NODE_ENV === 'development') {
                    console.error('Failed to load initial prompt from sessionStorage', error);
                }
            }
            hasSubmittedRef.current = true;
        }
    }, [
        chatId,
        sendMessage,
        messages.length,
        scrollToBottom,
        isViewOnly
    ]);
    // Register artifacts from messages to the store
    useEffect(()=>{
        const addArtifact = useChatArtifactStore.getState().addArtifact;
        const allMessages = [
            ...initialMessages || [],
            ...messages
        ];
        // Collect all artifact data from messages
        allMessages.forEach((message)=>{
            if (!message?.parts) return;
            message.parts.forEach((part)=>{
                const partObj = part;
                if (partObj.type === 'data-artifact' && partObj.data) {
                    const parseResult = artifactDataSchema.safeParse(partObj.data);
                    if (parseResult.success) {
                        const artifactData = parseResult.data;
                        addArtifact({
                            id: artifactData.id,
                            title: artifactData.title,
                            kind: artifactData.kind,
                            content: artifactData.content,
                            progressCallId: artifactData.progressCallId,
                            sourceAttachmentId: artifactData.sourceAttachmentId,
                            sourceUrl: artifactData.sourceUrl,
                            contentType: artifactData.contentType,
                            createdAt: new Date().toISOString(),
                            updatedAt: new Date().toISOString()
                        });
                    }
                }
            });
        });
    }, [
        messages,
        initialMessages
    ]);
    // Handle optimistic save events
    useEffect(()=>{
        if (saveEvents.length === 0) return;
        const ev = saveEvents[saveEvents.length - 1];
        // Type guard to validate save event structure
        const isSaveEvent = (event)=>{
            return typeof event === 'object' && event !== null && (typeof event.id === 'string' || event.id === undefined) && (typeof event.text === 'string' || event.text === undefined) && (typeof event.documentId === 'string' || event.documentId === undefined);
        };
        if (!isSaveEvent(ev)) {
            // Invalid event structure - clear and continue
            if (process.env.NODE_ENV === 'development') {
                console.error('Invalid save event structure', ev);
            }
            clearSaveEvents();
            return;
        }
        const optimistic = {
            id: typeof ev.id === 'string' ? ev.id : `save-${Date.now()}`,
            role: 'assistant',
            parts: [
                {
                    type: 'text',
                    text: typeof ev.text === 'string' ? ev.text : 'Saved document'
                },
                {
                    type: 'data-artifact',
                    data: {
                        id: typeof ev.documentId === 'string' ? ev.documentId : '',
                        title: typeof ev.title === 'string' ? ev.title : 'Untitled',
                        kind: typeof ev.kind === 'string' ? ev.kind : 'sheet',
                        content: typeof ev.content === 'string' ? ev.content : ''
                    }
                }
            ],
            metadata: {
                createdAt: new Date().toISOString()
            }
        };
        emitToolEvent({
            type: 'data-chat-log-save-optimistic',
            message: JSON.parse(JSON.stringify(optimistic))
        });
        clearSaveEvents();
    }, [
        saveEvents,
        clearSaveEvents
    ]);
    const handleSubmit = useCallback(async (data)=>{
        if (isViewOnly) return;
        const trimmedPrompt = data.prompt.trim();
        const hasAttachments = Array.isArray(data.attachments) && data.attachments.length > 0;
        if (!trimmedPrompt && !hasAttachments) return;
        setIsSending(true);
        const parts = [];
        if (trimmedPrompt.length > 0) {
            parts.push({
                type: 'text',
                text: trimmedPrompt
            });
        }
        if (hasAttachments && data.attachments) {
            parts.push({
                type: 'data-attachments',
                data: data.attachments.map((a)=>({
                        id: a.id,
                        name: a.name,
                        contentType: a.contentType
                    }))
            });
        }
        if (parts.length > 0) {
            isAtBottomRef.current = true;
            scrollToBottom('auto');
            const sending = sendMessage({
                parts
            });
            requestAnimationFrame(()=>scrollToBottom('auto'));
            await sending;
        }
    }, [
        sendMessage,
        scrollToBottom,
        isViewOnly
    ]);
    const handleComposerSubmit = useCallback(async (data)=>{
        if (isMountedRef.current) {
            await handleSubmit({
                prompt: data.prompt,
                files: data.files || [],
                attachments: data.attachments
            });
        }
    }, [
        handleSubmit
    ]);
    // Stop the in-flight stream. shanksChat.stop() aborts, which skips the
    // hook's onFinish/onError, so we reset isSending here to release the
    // composer back to its Send state.
    const handleStop = useCallback(()=>{
        void shanksChat.stop().then((stopped)=>{
            if (stopped) setIsSending(false);
            queryClient.invalidateQueries({
                queryKey: conversationQueryKey(chatId)
            });
        });
    }, [
        shanksChat,
        queryClient,
        chatId
    ]);
    // Replay the last message after a failure. onFinish/onError reset
    // isSending once the retried stream settles.
    const handleRetry = useCallback(()=>{
        setIsSending(true);
        shanksChat.retry();
    }, [
        shanksChat
    ]);
    // Get drawer state for layout adjustments
    const { isOpen: isDrawerOpen, isFullscreen } = useArtifactDrawer();
    // Calculate chat area max width to prevent content from hiding under drawer
    // Overlay artifacts need a constrained column. Office is a flex sibling,
    // so its resize already changes the chat's available width.
    const getChatMaxWidth = ()=>{
        if (isFullscreen) return ''; // No max-width in fullscreen, sidebar handles it
        if (isDrawerOpen) return 'max-w-lg'; // 512px
        // One centered 860px reading column for the thread and the composer.
        return 'mx-auto w-full max-w-[860px]';
    };
    // Streaming phase sentinel for tests: chat-stream-loading while a
    // Chat RPC is in flight, chat-stream-idle once it completes AND at
    // least one message has landed. Withholding the idle sentinel until
    // there's a message prevents the ui-cli `chat` wait from exiting
    // immediately on mount (when isLoading is briefly false before the
    // sessionStorage-driven auto-send fires).
    let phaseTestid;
    if (responseBusy) {
        phaseTestid = Testids.assistant.chatStreamLoading;
    } else if (thread.length > 0) {
        phaseTestid = Testids.assistant.chatStreamIdle;
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "flex h-full",
        children: [
            phaseTestid && /*#__PURE__*/ _jsx("span", {
                "data-testid": phaseTestid,
                "aria-hidden": "true",
                className: "hidden"
            }),
            /*#__PURE__*/ _jsx("output", {
                className: "sr-only",
                "aria-live": "polite",
                "data-testid": Testids.assistant.liveStatus,
                children: liveStatus
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "flex flex-col flex-1 min-w-0 bg-background",
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex-shrink-0 border-b border-border bg-background",
                        children: /*#__PURE__*/ _jsxs("div", {
                            "data-testid": Testids.assistant.toolbar,
                            className: cn('flex flex-wrap items-center gap-2.5 px-6 py-[13px]', // Only constrain the row when the artifact drawer overlays the
                            // right edge, so the actions otherwise sit flush right.
                            isDrawerOpen && !isFullscreen && 'max-w-lg'),
                            children: [
                                showRailToggle && /*#__PURE__*/ _jsx("button", {
                                    type: "button",
                                    onClick: toggleChatRail,
                                    "aria-label": chatRailCollapsed ? 'Show chat list' : 'Hide chat list',
                                    "aria-pressed": !chatRailCollapsed,
                                    title: "Toggle chat list",
                                    className: "flex flex-shrink-0 rounded p-[3px] text-muted-foreground transition-colors hover:bg-muted hover:text-foreground",
                                    children: chatRailCollapsed ? /*#__PURE__*/ _jsx(PanelLeftOpen, {
                                        "aria-hidden": "true",
                                        className: "size-3.5"
                                    }) : /*#__PURE__*/ _jsx(PanelLeftClose, {
                                        "aria-hidden": "true",
                                        className: "size-3.5"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(ChatTitle, {
                                    chatId: chatId,
                                    title: displayTitle,
                                    persisted: conversationPersisted,
                                    editable: conversationPersisted && isOwner && !isViewOnly
                                }),
                                projectId ? /*#__PURE__*/ _jsx(ChatProjectBadge, {
                                    projectId: projectId
                                }) : /*#__PURE__*/ _jsx(SpecraBadge, {
                                    className: "flex-shrink-0 uppercase tracking-[0.05em]",
                                    variant: "neutral",
                                    children: "Assistant"
                                }),
                                showPins ? /*#__PURE__*/ _jsx(ChatPins, {
                                    pins: pins,
                                    onChange: setPins,
                                    options: pinOptions,
                                    onOpen: ()=>setPinPickerOpened(true),
                                    notice: captureNotice,
                                    loading: captureLoading
                                }) : null,
                                /*#__PURE__*/ _jsxs("div", {
                                    "data-testid": Testids.assistant.toolbarActions,
                                    className: "ml-auto flex flex-shrink-0 items-center gap-2",
                                    children: [
                                        memoryChip ? /*#__PURE__*/ _jsx("span", {
                                            className: "hidden max-w-[14rem] truncate text-xs text-muted-foreground sm:inline",
                                            "data-testid": Testids.memory.chip,
                                            title: memoryChip,
                                            children: memoryChip
                                        }) : null,
                                        /*#__PURE__*/ _jsx(SpecraIconButton, {
                                            "data-testid": Testids.memory.openButton,
                                            label: "Open memory notes",
                                            onClick: ()=>setShowMemoryPanel(true),
                                            size: "sm",
                                            title: "Memory notes",
                                            variant: "secondary",
                                            children: /*#__PURE__*/ _jsx(Brain, {
                                                "aria-hidden": "true"
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            "data-testid": Testids.assistant.toSidebar,
                                            iconLeft: /*#__PURE__*/ _jsx(PanelRight, {
                                                "aria-hidden": "true"
                                            }),
                                            onClick: handleToSidebar,
                                            size: "sm",
                                            title: "Pop out to side panel (⌘J)",
                                            variant: "secondary",
                                            children: "To sidebar"
                                        }),
                                        isOwner && !isViewOnly && (!showPins || pins.length === 0) && /*#__PURE__*/ _jsx(ShareButton, {
                                            chatId: chatId,
                                            chatTitle: displayTitle
                                        }),
                                        isOwner && !isViewOnly && /*#__PURE__*/ _jsx(SpecraIconButton, {
                                            "data-testid": Testids.chatSettings.openButton,
                                            label: "Open chat settings",
                                            onClick: ()=>setShowSettings(true),
                                            size: "sm",
                                            variant: "secondary",
                                            children: /*#__PURE__*/ _jsx(Settings, {
                                                "aria-hidden": "true"
                                            })
                                        }),
                                        onClose && /*#__PURE__*/ _jsx(SpecraIconButton, {
                                            "data-testid": Testids.assistant.closeChat,
                                            label: "Close chat",
                                            onClick: onClose,
                                            size: "sm",
                                            variant: "secondary",
                                            children: /*#__PURE__*/ _jsx(X, {
                                                "aria-hidden": "true"
                                            })
                                        })
                                    ]
                                })
                            ]
                        })
                    }),
                    isViewOnly && accessContext?.sharedBy && accessContext?.sharedAt && /*#__PURE__*/ _jsx(ViewOnlyBanner, {
                        sharedBy: accessContext.sharedBy,
                        sharedAt: accessContext.sharedAt
                    }),
                    isViewOnly && !(accessContext?.sharedBy && accessContext?.sharedAt) && /*#__PURE__*/ _jsx("output", {
                        className: "block px-4 py-2 text-sm text-muted-foreground",
                        children: "View only — you can read this conversation."
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "relative flex-1 min-h-0",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                ref: scrollContainerRef,
                                "data-testid": Testids.assistant.scrollContainer,
                                className: "h-full overflow-y-auto overflow-x-hidden px-8 py-7",
                                children: /*#__PURE__*/ _jsxs("div", {
                                    className: getChatMaxWidth(),
                                    children: [
                                        /*#__PURE__*/ _jsx(ChatThread, {
                                            messages: thread,
                                            isSending: responseBusy,
                                            chatId: chatId,
                                            variant: variant,
                                            readOnly: isViewOnly,
                                            onFeedback: sendCardFeedback
                                        }),
                                        error && /*#__PURE__*/ _jsxs("div", {
                                            role: stoppedSubmission || incompleteSubmission ? 'status' : 'alert',
                                            className: `mt-4 p-4 border rounded-lg flex items-start gap-2 ${stoppedSubmission || incompleteSubmission ? 'bg-muted border-border' : 'bg-destructive/10 border-destructive/20'}`,
                                            children: [
                                                !stoppedSubmission && !incompleteSubmission && /*#__PURE__*/ _jsx(AlertTriangle, {
                                                    className: "w-5 h-5 text-destructive mt-0.5"
                                                }),
                                                /*#__PURE__*/ _jsxs("div", {
                                                    className: "flex-1",
                                                    children: [
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: stoppedSubmission || incompleteSubmission ? 'font-medium' : 'font-medium text-destructive',
                                                            children: incompleteSubmission ? 'Request incomplete' : stoppedSubmission ? 'Response stopped' : 'Error'
                                                        }),
                                                        /*#__PURE__*/ _jsx("div", {
                                                            className: stoppedSubmission || incompleteSubmission ? 'text-sm text-muted-foreground' : 'text-sm text-destructive/80',
                                                            children: error.message
                                                        }),
                                                        /*#__PURE__*/ _jsxs("button", {
                                                            type: "button",
                                                            onClick: handleRetry,
                                                            "data-testid": Testids.assistant.retryButton,
                                                            className: `mt-2 inline-flex items-center gap-1 text-sm font-medium hover:underline ${stoppedSubmission || incompleteSubmission ? 'text-foreground' : 'text-destructive'}`,
                                                            children: [
                                                                /*#__PURE__*/ _jsx(RotateCcw, {
                                                                    className: "w-4 h-4"
                                                                }),
                                                                incompleteSubmission ? 'Continue' : 'Retry'
                                                            ]
                                                        })
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                })
                            }),
                            showScrollButton && /*#__PURE__*/ _jsx("button", {
                                type: "button",
                                onClick: ()=>{
                                    isAtBottomRef.current = true;
                                    scrollToBottom('auto');
                                },
                                "data-testid": Testids.assistant.scrollToBottomButton,
                                "aria-label": "Scroll to latest",
                                className: "absolute bottom-4 left-1/2 -translate-x-1/2 z-10 rounded-full border border-border bg-card p-2 text-muted-foreground shadow-md transition-colors hover:bg-muted",
                                children: /*#__PURE__*/ _jsx(ArrowDown, {
                                    className: "w-5 h-5"
                                })
                            })
                        ]
                    }),
                    !isViewOnly && /*#__PURE__*/ _jsx("div", {
                        className: "flex-shrink-0 border-t border-border bg-background px-8 pt-3.5 pb-[18px]",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: getChatMaxWidth(),
                            children: /*#__PURE__*/ _jsx("div", {
                                "data-testid": variant === 'specra' ? Testids.specraChat.composer : undefined,
                                children: /*#__PURE__*/ _jsx(ComposerInput, {
                                    onSubmit: handleComposerSubmit,
                                    onStop: handleStop,
                                    onRemember: handleRememberDraft,
                                    rememberDisabled: responseBusy || memoriesQuery.isLoading,
                                    submitting: responseBusy,
                                    mode: "chat",
                                    submitLabel: "Send"
                                })
                            })
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(ArtifactEditorDrawer, {
                isOpen: isOpen,
                onClose: closeDrawer,
                messages: thread,
                isSending: responseBusy,
                chatId: chatId,
                onChatSubmit: handleComposerSubmit,
                onFeedback: sendCardFeedback,
                onStop: handleStop,
                readOnly: isViewOnly
            }),
            /*#__PURE__*/ _jsx(OfficeDocumentDrawer, {}),
            /*#__PURE__*/ _jsx(ChatSettings, {
                chatId: conversationPersisted ? chatId : '',
                isOpen: showSettings,
                onClose: ()=>setShowSettings(false)
            }),
            /*#__PURE__*/ _jsx(MemoryPanel, {
                envelope: chatEnvelope,
                isOpen: showMemoryPanel,
                onClose: ()=>setShowMemoryPanel(false),
                draft: memoryDraft,
                onDraftConsumed: ()=>setMemoryDraft(undefined)
            })
        ]
    });
});
export const ChatView = /*#__PURE__*/ memo(function ChatView(props) {
    return(// OnlyOfficeConnectorProvider must wrap BOTH the thread (which renders the
    // write cards) and the drawer (which registers the connector) so they share
    // one connector instance. OfficeDocumentDrawerProvider carries which
    // standalone document is open.
    /*#__PURE__*/ _jsx(OnlyOfficeConnectorProvider, {
        children: /*#__PURE__*/ _jsx(OfficeDocumentDrawerProvider, {
            children: /*#__PURE__*/ _jsx(ArtifactDrawerProvider, {
                children: /*#__PURE__*/ _jsx(ChatViewContent, {
                    ...props
                })
            })
        })
    }));
});
